import { Injectable } from '@angular/core';
import { IFacilityType } from '@app/shared/interfaces/IFacilityType';
import { ApiService } from './api.service';
import {
  SPFInfo,
  refStatewideAverages,
  refStatewideProportions,
} from '@app/shared/interfaces/ISPFInfo';
import {
  CountermeasureBCA,
  Intersection,
  SPFSiteInfo,
  Segment,
} from 'countermeasure-bca/src/countermeasure-bca';
import { IInvestigation } from '@app/shared/interfaces/IInvestigation';
import {
  ICountermeasureIntersection,
  ICountermeasureRamp,
  ICountermeasureSegment,
} from '@app/shared/interfaces/ICountermeasureLocation';
import { ISite } from '@app/shared/interfaces/ISite';
import { EvaluationDetails } from '../../shared/models/EvaluationDetails';
import { crashCount } from '@app/shared/models/crashCount';
import {
  BCSINFO,
  SITEKEY,
} from '@app/modules/investigation/components/countermeasures-grid/countermeasure-const';

@Injectable({
  providedIn: 'root',
})
export class FacilityTypesHelpers {
  public allFacilityTypes: IFacilityType[] = null;
  public facilityTypes: IFacilityType[] = null;
  public spfs: SPFInfo[] = null;
  public refStatewideAverages: refStatewideAverages[] = null;
  public refStatewideProportions: refStatewideProportions[] = null;

  constructor(private api: ApiService) {}

  public async loadFacilityTypes() {
    if (!(this.facilityTypes && this.allFacilityTypes)) {
      this.facilityTypes = await this.api.getFacilityTypes().toPromise();
      this.allFacilityTypes = await this.api.getAllFacilityTypes().toPromise();
    }
    if (!this.spfs) {
      this.spfs = await this.api.getSPFs().toPromise();
    }
    if (!this.refStatewideAverages) {
      this.refStatewideAverages = await this.api
        .getStatewideAverages()
        .toPromise();
    }
    if (!this.refStatewideProportions) {
      this.refStatewideProportions = await this.api
        .getStatewideProportionsList()
        .toPromise();
    }
  }

  public TestChildFacilityType(AssignedFacilityTypeID): IFacilityType {
    if (this.allFacilityTypes) {
      const FacilityType = this.allFacilityTypes.find(
        (type) => type.FacilityTypeID === AssignedFacilityTypeID
      );
      return FacilityType;
    }
    return null;
  }

  public getFacilityTypeDescription(facilityTypeID: number) {
    let facilityType = this.allFacilityTypes.find(
      (type) => type.FacilityTypeID === facilityTypeID
    );
    if (facilityType) {
      return facilityType.Description;
    } else {
      return 'Unknown Facility Type';
    }
  }

  public findApplicableSPFs(location: ISite, siteType: string): SPFSiteInfo {
    let applicableSPFs = this.spfs.filter((spf) => spf.ScreenType === siteType);
    let applicableFacilitySPF = [];
    const facType = this.allFacilityTypes.find(
      (facilityType) => facilityType.FacilityTypeID === location.FacilityTypeID
    );
    // currently in the db, there are no facility type derived SPFs for ramps and segments,
    // there might or might not be one for intersections
    if (facType && location.FacilityTypeID) {
      applicableFacilitySPF = applicableSPFs.filter(
        (spf) => spf.FacilityTypeId === location.FacilityTypeID.toString()
      );
    }

    //Removing this because we are should not be using SPF default and just return null
    /*if (applicableFacilitySPF.length === 0) {
      applicableSPFs = applicableSPFs.filter(
        (spf) => spf.FacilityTypeId === '*'
      );
    } else {
      applicableSPFs = applicableFacilitySPF;
    }*/
    if (siteType.toLowerCase() === 'int') {
      // no aadt = use 0 for HasAADT
      // has major/AADT_1 = use 1 for HasAADT
      // has major/AADT_1 + minor/AADT_2 = use 2 for HasAADT
      const aadt =
        (location as ICountermeasureIntersection).AADT_1 ||
        location.AADTMajorRoad
          ? (location as ICountermeasureIntersection).AADT_2 ||
            location.AADTMinorRoad
            ? 2
            : 1
          : 0;
      if (facType) {
        applicableFacilitySPF = applicableFacilitySPF.filter(
          (spf) => spf.HasAADT === aadt
        );
      }
    } else {
      const aadt = location.AADT ? 1 : 0;
      if (facType) {
        applicableFacilitySPF = applicableFacilitySPF.filter(
          (spf) => spf.HasAADT === aadt
        );
      }
    }

    if (applicableFacilitySPF.length !== 2) {
      //this.loading.clearLoading();
      console.warn('SPF lookup failed for ' + location);
      return new SPFSiteInfo(null, null);
    }
    const tot = applicableFacilitySPF.find((spf) => spf.Severity === 'Tot');
    const fi = applicableFacilitySPF.find((spf) => spf.Severity === 'FI');
    const spfSiteInfo = new SPFSiteInfo(tot, fi);
    return spfSiteInfo;
  }

  /**This method will Calculate the PSI_Tot:
   * @remarks
   * There are two ways to calculate the PSI.
   * The first way it to calculate ExcessCrashRate_Total the second is using StateWideAverage
   * @example
   * Method 1: ExcessCrashRate_Total -
   * ```ts
   * pred_Tot = TotalPredictedCrashes * SiteProportion;
   * w_Tot = 1 /(1 + K_Total * pred_Tot * YearsInStudy);
   * CummExp_Tot = w_Tot * pred_Tot * YearsInStudy + (1 - w_Tot) * TotalCrashes;
   * PSI = CummExp_Tot - pred_Tot;
   * ```
   * @param facilityTypeID - This is the facility Type for the site
   * @param TotalCrashes - Total amount of crashes for the site
   * @param Years - Total number of years for the investigation
   * @param FromYear - Start year for the investigation
   * @param ToYear - End year for the investigation
   * @param HasAADT - Volume for the site
   * @param countermeasureBCA - The countermeasure variable that need basic information to do basic calculation and spf info
   * @param Investigation - Investigation used in the calculation
   * @returns PSI_Tot
   */
  public CalculatePSI_Total(
    HasAADT: boolean,
    countermeasureBCA: CountermeasureBCA
  ) {
    //NOTE: Removed sitewide averages calculation for and replaced them with the GIS analysis algorithm which is
    //expected - predicted
    let PSI =
      countermeasureBCA.Site.AnnualTotalExpectedCrashes -
      countermeasureBCA.Site.TotalPredictedCrashes;

    return PSI;
  }

  public CalculatePSI_KA(
    HasAADT: boolean,
    countermeasureBCA: CountermeasureBCA
  ) {
    //NOTE: Removed sitewide averages calculation for and replaced them with the GIS analysis algorithm which is
    //expected - predicted
    let PSI_KA =
      countermeasureBCA.Site.AnnualKAExpectedCrashes -
      countermeasureBCA.Site.KAPredictedCrashesProportion;

    return PSI_KA;
  }

  public CalculatePSI_FI(
    HasAADT: boolean,
    countermeasureBCA: CountermeasureBCA
  ) {
    //NOTE: Removed sitewide averages calculation for and replaced them with the GIS analysis algorithm which is
    //expected - predicted
    let PSI_FI =
      countermeasureBCA.Site.AnnualFIExpectedCrashes -
      countermeasureBCA.Site.FIPredictedCrashes;

    return PSI_FI;
  }

  public getProportion(
    facilityTypeID,
    CrashCount: crashCount,
    FromYear: number,
    ToYear: number
  ) {
    let statewideProportion = this.refStatewideProportions.filter(
      (x) =>
        x.FacilityTypeID == facilityTypeID &&
        x.CaseYear >= FromYear &&
        x.CaseYear <= ToYear
    );
    const sum = statewideProportion.reduce(
      (a, b) =>
        a +
        ((CrashCount.StudyK ? b.K : 0) +
          (CrashCount.StudyA ? b.A : 0) +
          (CrashCount.StudyB ? b.B : 0) +
          (CrashCount.StudyC ? b.C : 0) +
          (CrashCount.StudyO ? b.O : 0)),
      0
    );
    const tot = statewideProportion.reduce(
      (a, b) => a + (b.K + b.A + b.B + b.C + (CrashCount.StudyO ? b.O : 0)),
      0
    );
    if (tot != 0) {
      return (1.0 * sum) / tot;
    }
    return 0;
  }

  public getKAProportion(
    facilityTypeID,
    CrashCount: crashCount,
    FromYear: number,
    ToYear: number
  ) {
    let statewideProportion = this.refStatewideProportions.filter(
      (x) =>
        x.FacilityTypeID == facilityTypeID &&
        x.CaseYear >= FromYear &&
        x.CaseYear <= ToYear
    );
    const sum = statewideProportion.reduce(
      (a, b) =>
        a + ((CrashCount.StudyK ? b.K : 0) + (CrashCount.StudyA ? b.A : 0)),
      0
    );
    const tot = statewideProportion.reduce(
      (a, b) => a + (b.K + b.A + b.B + b.C),
      0
    );
    if (tot != 0) {
      return (1.0 * sum) / tot;
    }
    return 0;
  }

  private async getCMFInfoForEvalaution(
    location: ISite,
    EvaluationDetails: EvaluationDetails,
    CrashCount: crashCount,
    Before: boolean
  ) {
    let siteType: 'Seg' | 'Int' | 'Ramp';
    if (location.MasterIntersectionID || location.ScreenedIntersectionID) {
      siteType = 'Int';
    } else if (location.RampID || location.ScreenedRampID) {
      siteType = 'Ramp';
    } else {
      siteType = 'Seg';
    }

    // find applicable SPFs for this location
    // Moved to load SPF here to take into consideration of volume
    const spfSiteInfo = this.findApplicableSPFs(location, siteType);

    let countermeasureBCA: CountermeasureBCA;

    let YearOfStudy;
    let FromDate;
    let ToDate;
    if (Before) {
      FromDate = EvaluationDetails.FromDate;
      ToDate = EvaluationDetails.ConstructionStartDate;
    } else {
      FromDate = EvaluationDetails.ConstructionCompletionDate;
      ToDate = EvaluationDetails.ToDate;
    }

    YearOfStudy =
      new Date(EvaluationDetails.FromDate).getFullYear() -
      new Date(EvaluationDetails.ConstructionStartDate).getFullYear();

    if (siteType === 'Seg' || siteType === 'Ramp') {
      countermeasureBCA = new CountermeasureBCA(
        YearOfStudy,
        null,
        null,
        new Segment({
          TotalCrashCount: location.CrashCounts.TotalCrashCount,
          PDOCrashCount: location.CrashCounts.PDOCrashCount,
          FICrashCount: location.CrashCounts.FICrashCount,
          SeriousInjuryCrashCount: location.CrashCounts.SeriousInjuryCrashCount,
          FatalCrashCount: location.CrashCounts.FatalCrashCount,
          AADT_1: location.AADT,
          SegmentLength: (location as ICountermeasureSegment).SegmentLength, // * 0.00062137, // converts meters to miles,
          SPF: spfSiteInfo,
        })
      );
    } else if (siteType === 'Int') {
      countermeasureBCA = new CountermeasureBCA(
        YearOfStudy,
        null,
        null,
        new Intersection({
          TotalCrashCount: location.CrashCounts.TotalCrashCount,
          PDOCrashCount: location.CrashCounts.PDOCrashCount,
          FICrashCount: location.CrashCounts.FICrashCount,
          SeriousInjuryCrashCount: location.CrashCounts.SeriousInjuryCrashCount,
          FatalCrashCount: location.CrashCounts.FatalCrashCount,
          AADT_1: location.AADTMajorRoad,
          AADT_2: location.AADTMinorRoad,
          SPF: spfSiteInfo,
        })
      );
    }

    if (location.FacilityTypeID && spfSiteInfo && spfSiteInfo.TotalSPFInfo) {
      location['PSI'] = this.CalculatePSI_Total(
        location.AADT || location.AADTMajorRoad ? true : false,
        countermeasureBCA
      );
      location['PSI_KA'] = this.CalculatePSI_KA(
        location.AADT || location.AADTMajorRoad ? true : false,
        countermeasureBCA
      );
      location['PSI_FI'] = this.CalculatePSI_FI(
        location.AADT || location.AADTMajorRoad ? true : false,
        countermeasureBCA
      );
    } else {
      location['PSI'] = null;
      location['PSI_KA'] = null;
      location['PSI_FI'] = null;
    }
    location[BCSINFO] = countermeasureBCA;
    location[SITEKEY] = countermeasureBCA.Site;
    return location;
  }
}
